import React, { useState, useEffect, useRef } from 'react'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import TextInput from '@jimmyboys/liita-ui/dist/components/TextInput'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'

import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import { Link } from 'react-router-dom'
import { Search } from 'models'
import styles from './SearchInput.module.sass'
import Dropdown from '@jimmyboys/liita-ui/dist/components/Dropdown'

const SearchInput = (props) => {
	const { className, ...attributes } = props
	const { i18n, expireSession } = useGlobal()
	const [searchQuery, setSearchQuery] = useState('')
	const [focus, setFocus] = useState(false)
	const [results, setResults] = useState([])
	const [searching, setSearching] = useState(false)
	const [searchType, setSearchType] = useState('processId')
	const timeout = useRef(null)
	const containerRef = useRef(null)

	const searchOptions = [
		{
			value: 'processId',
			text: i18n`Trust Process ID`,
		},
		{
			value: 'injuredName',
			text: i18n`Injured name`,
		},
		{
			value: 'requisitionId',
			text: i18n`Requisition ID`,
		},
		{
			value: 'processNo',
			text: i18n`Process no`,
		},
	]

	useEffect(() => {
		if (!searchQuery.length) {
			return
		}
		const controller = new AbortController()
		setSearching(true)
		fetch(
			`${API_URL}/api/search/?q=${searchQuery}&type=${searchType}`,
			fetchOptions({
				signal: controller.signal,
			})
		)
			.then((r) => parseJSONContent(r))
			.then((r) => {
				const results =
					searchType === 'requisitionId'
						? (r || [])
								.reduce((acc, elem) => {
									const mappedReq = elem.requisitions?.map(
										(item) => ({
											...elem,
											requisitions: undefined,
											requisitionId: item.id,
										})
									)

									return acc.concat(mappedReq)
								}, [])
								.map((json) => Search.fromJSON(json))
								.slice(0, 10)
						: (r || []).map((json) => Search.fromJSON(json))

				setSearching(false)
				setResults(results)
			})
			.catch((e) => {
				if (e.status === '401' || e.status === 401) {
					expireSession()
				}

				setSearching(false)
				setResults([])
			})
		return () => {
			setSearching(false)
			controller.abort()
		}
	}, [expireSession, searchQuery, searchType])

	useEffect(() => {
		const timeoutId = timeout.current
		const handleClickOutside = (e) => {
			if (!containerRef.current.contains(e.target)) {
				setFocus(false)
			}
		}
		document.body.addEventListener('click', handleClickOutside, true)
		return () => {
			document.body.removeEventListener('click', handleClickOutside, true)
			clearTimeout(timeoutId)
		}
	}, [])

	return (
		<div
			{...attributes}
			ref={containerRef}
			className={`${styles['root'] || ''} ${className || ''} ${
				focus ? styles['is-focus'] : ''
			}`}
		>
			<Dropdown
				className={styles['dropdown-type']}
				value={searchType}
				options={searchOptions}
				onChange={(e) => {
					setSearchType(e.target.value)
				}}
			/>
			<div className={styles['search-input']}>
				<TextInput
					placeholder={i18n`Search`}
					className={styles['text-input']}
					debounce={500}
					onDebounce={({ target: { value } }) => {
						if (searchType !== 'injuredName') {
							setSearchQuery(() => value.replace(/[^0-9\w]/g, ''))
						} else {
							setSearchQuery(value)
						}
					}}
					onFocus={() => {
						setFocus(true)
					}}
				/>
				<Icon
					className={styles['input-icon']}
					name='loupe'
					width='18px'
					height='18px'
				/>
				<div className={styles['result-list']}>
					<h6>{i18n`Search results`}</h6>
					{searching && (
						<small
							className={styles['info-label']}
						>{i18n`Searching`}</small>
					)}
					{!searching && !results.length && (
						<small
							className={styles['info-label']}
						>{i18n`Injured name, process ID or process no.`}</small>
					)}
					{!searching &&
						results.map((process, index) => (
							<div className={styles['list-item']} key={index}>
								<Link
									onClick={() => {
										clearTimeout(timeout.current)
										setFocus(false)
									}}
									to={
										searchType !== 'requisitionId'
											? `/clinical-processes/${process.id}/appointments`
											: `/clinical-processes/${process.id}#R_${process.requisitionId}`
									}
								>
									<h5>
										{searchType === 'processNo'
											? process?.insurancePolicy
													?.externalId
											: searchType === 'requisitionId'
											? process.requisitionId
											: process.id}
										: {process.injured?.name}
									</h5>
									<small>
										{
											process.info?.createdAt
												?.shortDateFormat
										}{' '}
										{
											process.accident?.segment
												?.abbreviation
										}{' '}
										{
											process.insurancePolicy
												?.insuranceCompany?.shortName
										}{' '}
										{process.insurancePolicy?.externalId}
									</small>
								</Link>
							</div>
						))}
				</div>
			</div>
		</div>
	)
}

export default SearchInput
